import {
  ref,
  onValue,
  push,
  onDisconnect,
  set,
  update,
  remove,
  serverTimestamp,
} from "firebase/database";

export default function createPresenceTrackerRealtime(config = {}) {
  const {
    rtdb,
    idUsuario,
    idEmpresa = "",
    rutaInicial = "/",
    basePath = "riskmann_registro_estado_usuarios",
    debug = false,
  } = config;

  if (!rtdb) throw new Error("rtdb es requerido");
  if (!idUsuario) throw new Error("idUsuario es requerido");

  let iniciado = false;
  let idEmpresaActual = idEmpresa;
  let rutaActual = rutaInicial;
  let currentConnectionRef = null;
  let unsubscribeConnected = null;

  const userRootPath = `${basePath}/${idUsuario}`;
  const metaRef = ref(rtdb, `${userRootPath}/metadata`);
  const connectionsRef = ref(rtdb, `${userRootPath}/conexiones`);
  const lastOnlineRef = ref(rtdb, `${userRootPath}/ultimaConexion`);
  const connectedRef = ref(rtdb, ".info/conectado");

  const isBrowser = typeof window !== "undefined";
  const isActive = () =>
    typeof document === "undefined"
      ? true
      : document.visibilityState === "visible";

  function log(...args) {
    if (debug) console.log("[presenceTrackerRealtime]", ...args);
  }

  async function writeMeta(extra = {}) {
    await update(metaRef, {
      id: idUsuario,
      idEmpresa: idEmpresaActual,
      ruta: rutaActual,
      activo: isActive(),
      ultimaActualizacion: serverTimestamp(),
      ...extra,
    });
  }

  async function writeCurrentConnection() {
    if (!currentConnectionRef) return;

    await update(currentConnectionRef, {
      ruta: rutaActual,
      activo: isActive(),
      ultimaActualizacion: serverTimestamp(),
    });
  }

  async function attachConnection() {
    currentConnectionRef = push(connectionsRef);

    // Primero se registran las acciones server-side al desconectar
    await Promise.all([
      onDisconnect(currentConnectionRef).remove(),
      onDisconnect(lastOnlineRef).set(serverTimestamp()),
    ]);

    // Luego se marca la conexión como activa
    await writeMeta();
    await set(currentConnectionRef, {
      ultimaConexion: serverTimestamp(),
      ruta: rutaActual,
      activo: isActive(),
      ultimaActualizacion: serverTimestamp(),
    });

    log("Conexión registrada");
  }

  async function handleConnectedChange(snapshot) {
    const conectado = snapshot.val() === true;

    if (!conectado) {
      currentConnectionRef = null;
      log("Cliente RTDB sin conexión");
      return;
    }

    // Reconexión o primera conexión
    if (!currentConnectionRef) {
      await attachConnection();
      return;
    }

    await writeMeta();
    await writeCurrentConnection();
  }

  async function handleVisibilityOrFocus() {
    try {
      await writeMeta();
      await writeCurrentConnection();
    } catch (error) {
      console.error("[presenceTrackerRealtime]", error);
    }
  }

  function addWindowListeners() {
    if (!isBrowser) return;
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);
    window.addEventListener("blur", handleVisibilityOrFocus);
  }

  function removeWindowListeners() {
    if (!isBrowser) return;
    document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    window.removeEventListener("focus", handleVisibilityOrFocus);
    window.removeEventListener("blur", handleVisibilityOrFocus);
  }

  function start() {
    if (iniciado) return;
    iniciado = true;

    addWindowListeners();

    unsubscribeConnected = onValue(connectedRef, (snapshot) => {
      handleConnectedChange(snapshot).catch((error) => {
        console.error("[presenceTrackerRealtime]", error);
      });
    });

    log("Tracker iniciado");
  }

  async function stop(reason = "manual-stop") {
    if (!iniciado) return;
    iniciado = false;

    removeWindowListeners();

    if (typeof unsubscribeConnected === "function") {
      unsubscribeConnected();
      unsubscribeConnected = null;
    }

    try {
      await writeMeta({
        activo: false,
        desconexion: {
          razon: reason,
          ultimaActualizacion: serverTimestamp()
        }
      });

      if (currentConnectionRef) {
        await onDisconnect(currentConnectionRef).cancel();
        await remove(currentConnectionRef);
        currentConnectionRef = null;
      }

      await onDisconnect(lastOnlineRef).cancel();
      await set(lastOnlineRef, serverTimestamp());
    } catch (error) {
      console.error("[presenceTrackerRealtime]", error);
    }

    log("Tracker detenido:", reason);
  }

  function switchPage(nuevaRuta = "/") {
    rutaActual = nuevaRuta;
    if (iniciado) {
      handleVisibilityOrFocus();
    }
  }

  function getState() {
    return {
      iniciado,
      idUsuario,
      idEmpresa: idEmpresaActual,
      rutaActual,
      hasConnectionRef: !!currentConnectionRef,
    };
  }

  return {
    start,
    stop,
    switchPage,
    getState,
  };
}
